/**
 * Playground preview of the no-touch review gates. Evaluates every STP stage
 * (first STP, second STP, and agentic) for one order row and merges them into a
 * single result.
 */
import { getBearerToken } from "../../../../../../api/postCall/bearerToken";
import { CoverMyMedsInputModel } from "../../../../../../data-model/cmmInputRequestModel";
import { API_ENDPOINTS } from "../../../../../../enums/apiUrls";
import { getStpConfigDocumentPath, type StpVariant } from "../useFirstStpConfig";
import type { EvaluationApiResponse, EvaluationBlock } from "./playGroundTypes";

/** One review gate: its backend invocation token and the org-scoped rules document it reads. */
interface StpStage {
  stepId: string;
  label: string;
  invocation: string;
  variant: StpVariant;
}

/**
 * Stages previewed on every Run, in display order. `/pa-order-update-with-rules/run`
 * takes a single invocation per request, so each stage is its own call and all
 * three are fired in parallel.
 */
export const STP_STAGES: StpStage[] = [
  {
    stepId: "first-stp-review",
    label: "First STP review",
    invocation: "before_stp",
    variant: "first",
  },
  {
    stepId: "second-stp-review",
    label: "Second STP review",
    invocation: "before_second_stp",
    variant: "second",
  },
  {
    stepId: "agentic-review",
    label: "Agentic review",
    invocation: "after_second_stp",
    variant: "agentic",
  },
];

/** Per-stage verdict used to build the rendered block and the aggregated response. */
interface StageOutcome {
  block: EvaluationBlock;
  needsReview: string;
  reason: string;
  configLoaded: boolean;
}

const buildBlock = (
  stage: StpStage,
  status: string,
  reason: string,
): EvaluationBlock => ({
  block_label: stage.label,
  steps: [
    {
      step_id: stage.stepId,
      label: stage.label,
      status,
      active: true,
      blocked: status === "blocked",
      reason: reason || undefined,
    },
  ],
});

/**
 * Previews one stage's rules against the given row.
 *
 * @param stage - Stage to evaluate (invocation token and rules document)
 * @param payload - cmm_input row previewed in memory by the backend
 * @param orgId - Organization whose no-touch config is evaluated
 * @param token - Bearer token shared by all stage calls, when available
 * @returns Verdict for this stage
 * @throws {Error} When the request fails
 */
const evaluateStage = async (
  stage: StpStage,
  payload: CoverMyMedsInputModel,
  orgId: string,
  token: string | null,
): Promise<StageOutcome> => {
  const response = await fetch(API_ENDPOINTS.EVALUATE_FIRST_STP_REVIEW, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      invocation: stage.invocation,
      preview_rows: [payload],
      rules_paths: [getStpConfigDocumentPath(orgId, stage.variant)],
    }),
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const body = await response.json();

  // A skipped run echoes the input row back, so its needs_review is the stale
  // BigQuery value rather than a verdict from these rules.
  if (body?.skipped) {
    const skipReason = body?.message ?? "No rules matched this invocation";
    return {
      block: buildBlock(stage, "skipped", skipReason),
      needsReview: "Not Needed",
      reason: "",
      configLoaded: body?.success !== false,
    };
  }

  const updatedRow = body?.updated_rows?.[0];
  const needsReview = updatedRow?.needs_review ?? "Not Needed";
  const reviewReason = updatedRow?.review_reason ?? "";
  const status =
    needsReview === "Needed"
      ? "blocked"
      : needsReview === "Error"
        ? "error"
        : "passed";

  return {
    block: buildBlock(stage, status, reviewReason),
    needsReview,
    reason: reviewReason,
    configLoaded: body?.success !== false,
  };
};

const toError = (reason: unknown): Error =>
  reason instanceof Error ? reason : new Error(String(reason));

const failedOutcome = (stage: StpStage, reason: unknown): StageOutcome => ({
  block: buildBlock(stage, "error", toError(reason).message),
  needsReview: "Error",
  reason: `${stage.label}: ${toError(reason).message}`,
  configLoaded: false,
});

/** Needed anywhere wins over an errored stage, which wins over a clean run. */
const aggregateNeedsReview = (outcomes: StageOutcome[]): string => {
  if (outcomes.some((o) => o.needsReview === "Needed")) return "Needed";
  if (outcomes.some((o) => o.needsReview === "Error")) return "Error";
  return "Not Needed";
};

/**
 * Previews every STP review stage for one order row in parallel.
 *
 * A stage that fails on its own is surfaced as an errored block so the other
 * stages' verdicts still render; only an all-stage failure rejects.
 *
 * @param payload - cmm_input row to evaluate; must carry `org_id`
 * @returns Combined evaluation with one block per stage
 * @throws {Error} When the row has no `org_id`, or when every stage call fails
 */
export const evaluateStpReviewRules = async (
  payload: CoverMyMedsInputModel,
): Promise<EvaluationApiResponse> => {
  // The gates are org-scoped, so previewing without an org would evaluate a different
  // document than the RPA does — the divergence this playground exists to catch.
  if (!payload.org_id) {
    throw new Error(
      "This order has no org_id, so its no-touch config cannot be resolved",
    );
  }
  const orgId = payload.org_id;
  const token = await getBearerToken();

  const settled = await Promise.allSettled(
    STP_STAGES.map((stage) => evaluateStage(stage, payload, orgId, token)),
  );

  const firstFailure = settled.find((result) => result.status === "rejected");
  if (firstFailure && settled.every((result) => result.status === "rejected")) {
    throw toError((firstFailure as PromiseRejectedResult).reason);
  }

  const outcomes = settled.map((result, index) =>
    result.status === "fulfilled"
      ? result.value
      : failedOutcome(STP_STAGES[index], result.reason),
  );

  return {
    needs_review: aggregateNeedsReview(outcomes),
    reason: outcomes
      .map((outcome) => outcome.reason)
      .filter(Boolean)
      .join(" | "),
    config_loaded: outcomes.every((outcome) => outcome.configLoaded),
    matched_rule_label: STP_STAGES.map((stage) => stage.label).join(" + "),
    details: { blocks: outcomes.map((outcome) => outcome.block) },
  };
};
